import React, { useRef, useMemo } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ProductRatingContainer from '~/shared/components/ProductRatingContainer/ProductRatingContainer';
import ScrollOffset from '~/shared/components/ScrollOffset';
import SizeFabricGuideModal from '~/shared/components/SizeFabricGuideModal';
import { getBrandValue, isSavage } from '~/shared/utils/brandNameHelper';
import { isIneligibleFitGuideProduct } from '~/shared/utils/isIneligibleFitGuideProduct';
import { getKidsProductCategories } from '~/shared/utils/productCategoryHelpers';
import { shouldUseAlternateSizeGuide } from '~/shared/utils/shouldUseAlternateSizeGuide';
import { usePDPOptionLabels } from '~/shared/utils/useLabels';
import useProductDetailEvents from '~/shared/utils/useProductDetailEvents';
import SizeFabricGuideFableticsModal from '~/staticPages/components/SizeFabricGuideFableticsModal';
import {
  mobile,
  useBreakpoint,
  OptionLabelProvider,
  useProductDetailContext,
  Separator,
  SkeletonLine,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import GroupSizeSelector from '../GroupSizeSelector';
import HotComment from '../HotComment';
import OutfitItems from '../OutfitItems';
import ProductDetailColorSelector from '../ProductDetailColorSelector';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';
import ProductDetailEmbroiderySelector from '../ProductDetailEmbroiderySelector.js';
import ProductDetailOtherSelectors from '../ProductDetailOtherSelectors';
import ProductDetailPrice from '../ProductDetailPrice';
import ProductDetailQuantitySelector from '../ProductDetailQuantitySelector';
import ProductDetailSizeSelector from '../ProductDetailSizeSelector';
import ProductDetailsSection, {
  ProductDescription as BaseProductDescription,
} from '../ProductDetailsSection';
import ScrubsSizeGuideModal from '../ScrubsSizeGuideModal';

const ItemWrapper = styled.div`
  ${mobile`
    padding: 24px 24px 0 24px;
  `}
`;

const DetailsWrapper = styled.div`
  margin: 32px 0;
`;

const OutfitSection = styled.div`
  ${mobile`
    background: ${({ theme }) => theme.colors.pdpOutfitSectionBg};
    margin-bottom: 16px;
    padding: 16px;
  `}
`;

const OutfitMessageWrapper = styled.div`
  background: ${({ theme }) => theme.colors.palette.white};

  ${mobile`
    border-bottom: 1px solid ${({ theme }) =>
      theme.colors.productDetailsOutfitMessage};
  `}
`;

const OutfitItemCountMessage = styled.div`
  margin-bottom: 16px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.sublabelBlack};

  ${mobile`
    margin-top: 16px;
  `}
`;

const separatorStyle = css`
  margin: 16px 0px;
  color: ${({ theme }) => theme.colors.productDetailSeparator};
`;

const ProductDescription = styled(BaseProductDescription)`
  padding: 0 16px;
`;

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const RatingAndCalloutWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SkeletonLineWithMarginBottom = styled(SkeletonLine)`
  margin-bottom: ${(props) =>
    props.$marginBottom ? `${props.$marginBottom}px` : 0};
`;

const PriceWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.priceWrapper};
`;

const NotificationWrapper = styled.div`
  margin-bottom: 18px;
  ${mobile`
    padding: 16px;
  `}
`;

// this is currently a savage only component, if we ever need this for other brands,
// we should move these styles to typography or theme tokens.
const SXFSizeGuideCalloutWrapper = styled.div`
  font-size 12px;
  font-weight 300;
  letter-spacing: 0.3px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  margin-top: 16px;
  margin-bottom: 8px;
`;

const LineLoaderWithContainer = (props) => {
  return (
    <div>
      <SkeletonLineWithMarginBottom {...props} />
    </div>
  );
};

export const ProductDetailsLoading = () => {
  return (
    <>
      <LineLoaderWithContainer height={20} $marginBottom={12} width={200} />
      <LineLoaderWithContainer height={50} $marginBottom={24} />
      <LineLoaderWithContainer height={20} $marginBottom={12} width={75} />
      <LineLoaderWithContainer height={50} $marginBottom={10} />
      <LineLoaderWithContainer height={20} $marginBottom={32} width={200} />
      <LineLoaderWithContainer height={20} $marginBottom={6} width={50} />
      <LineLoaderWithContainer height={20} $marginBottom={12} width={200} />
      <LineLoaderWithContainer height={75} $marginBottom={24} />
    </>
  );
};

const scrubsId = config.get('public.productCategories.scrubs');

const shouldShowSizeGuideIcon = getBrandValue({
  savage: false,
  default: true,
});

export default function ProductDetails({
  productDetail: productDetailFromProps,
  productRef,
  showProductRating,
  relatedProductsLoading,
}) {
  const productDetailFromCtx = useProductDetailContext();
  const productDetail = productDetailFromProps || productDetailFromCtx;
  const product = productDetail.product;
  const numberOfPieces = product?.bundleComponentProducts?.length;
  const categoryIdList = product?.productCategoryIdList;
  const tagIdList = product?.tagIdList;
  const description = product?.description;
  const isMysteryBox = product?.isMysteryBundle;
  const isScrubs = categoryIdList?.includes(scrubsId);
  const showFitGuide =
    !productDetail.isBundle &&
    !isIneligibleFitGuideProduct(product) &&
    !isScrubs;
  useProductDetailEvents();

  const hasSXFSizeGuideCallout = useMemo(() => {
    return !isSavage()
      ? false
      : tagIdList?.includes(
          config.get('public.productTags.hasSXFSizeGuideCallout')
        );
  }, [tagIdList]);

  const kidsIds = getKidsProductCategories();
  const isKids = categoryIdList?.some((categoryId) =>
    kidsIds?.includes(categoryId)
  );

  const useAlternateSizeGuide = shouldUseAlternateSizeGuide(tagIdList);

  const reviewSectionRef = useRef();
  const { isMobile } = useBreakpoint();
  const showEnhancedLayout = config.get(
    'public.brand.showEnhancedProductLayout'
  );
  const useFableticsPDPFitGuide =
    config.get('public.brand.useFableticsPDPFitGuide') || isKids;

  const { showEmbroiderySelector } = useProductDetailEmbroideryContext();

  return (
    <OptionLabelProvider useLabels={usePDPOptionLabels}>
      {productDetail.isBundle ? (
        <>
          {description ? (
            <ProductDescription description={description} />
          ) : null}
          {!isMysteryBox && (
            <>
              <OutfitMessageWrapper>
                <OutfitItemCountMessage>
                  <FormattedMessage
                    id="site_product_page.outfit_includes"
                    defaultMessage="This {1}-Piece Outfit Includes:"
                    values={{
                      1: numberOfPieces,
                    }}
                  />
                </OutfitItemCountMessage>
              </OutfitMessageWrapper>

              <ScrollOffset ref={productRef} offset={10}>
                <OutfitSection>
                  <OutfitItems />
                </OutfitSection>
                {isMobile && !showEnhancedLayout && (
                  <ItemWrapper>
                    <ProductDetailPrice />
                  </ItemWrapper>
                )}
              </ScrollOffset>
            </>
          )}
          {isMysteryBox && (
            <>
              <GroupSizeSelector product={product} />
              <DetailsWrapper>
                <ProductDetailsSection product={product} />
              </DetailsWrapper>
              <NotificationWrapper>
                <HeaderTitle>
                  <FormattedMessage
                    id="site_product_page.important_note"
                    defaultMessage="Important Note:"
                  />
                </HeaderTitle>
                <FormattedMessage
                  id="site_product_page.mystery_box_final_sale"
                  defaultMessage="*All sales are final. No refunds, returns, or exchanges. Limited to one size selection per box."
                />
              </NotificationWrapper>
            </>
          )}
        </>
      ) : (
        <ScrollOffset ref={productRef} offset={10}>
          {showEnhancedLayout && isMobile && (
            <PriceWrapper>
              <RatingAndCalloutWrapper>
                {showProductRating && (
                  <ProductRatingContainer
                    productRef={productRef}
                    reviewSectionRef={reviewSectionRef}
                  />
                )}
              </RatingAndCalloutWrapper>
              <ProductDetailPrice />
            </PriceWrapper>
          )}
          <ItemWrapper>
            <ProductDetailColorSelector
              relatedProductsLoading={relatedProductsLoading}
              displayLoadingState
            />
            <ProductDetailSizeSelector />
            {hasSXFSizeGuideCallout && (
              <SXFSizeGuideCalloutWrapper>
                <FormattedMessage
                  id="site_pdp.size_guide_callout"
                  defaultMessage="Be sure to check out our size guide to find your best fit."
                />
              </SXFSizeGuideCalloutWrapper>
            )}
            {isScrubs && (
              <ScrubsSizeGuideModal
                categoryIdList={categoryIdList}
                showMessaging
                showSizeCalloutMessage={false}
                useAlternate={useAlternateSizeGuide}
              />
            )}
            {showFitGuide && !useFableticsPDPFitGuide && (
              <SizeFabricGuideModal
                useAlternate={useAlternateSizeGuide}
                shouldShowAssociatedIcon={shouldShowSizeGuideIcon}
              />
            )}
            {showFitGuide && useFableticsPDPFitGuide && (
              <SizeFabricGuideFableticsModal
                categoryIdList={categoryIdList}
                useAlternate={useAlternateSizeGuide}
                isKids={isKids}
              />
            )}
            <ProductDetailOtherSelectors />
            {showEmbroiderySelector && <ProductDetailEmbroiderySelector />}
            <ProductDetailQuantitySelector />
            {!showEnhancedLayout && isMobile && (
              <>
                <ProductDetailPrice />
                <Separator thickness={1} separatorStyle={separatorStyle} />
              </>
            )}
          </ItemWrapper>
          {showEnhancedLayout && (
            <DetailsWrapper>
              <ProductDetailsSection product={product} />
            </DetailsWrapper>
          )}
        </ScrollOffset>
      )}
      <HotComment product={product} />
    </OptionLabelProvider>
  );
}

ProductDetails.propTypes = {
  productDetail: PropTypes.object,
  productRef: PropTypes.any,
  relatedProductsLoading: PropTypes.bool,
  showProductRating: PropTypes.bool,
};
